import React, { useState, useEffect, useRef, useCallback } from "react";
import { MapPin, X, Loader2 } from "lucide-react";
import { suggestionApi } from "../services/api";
import { useDebounce } from "../hooks/useDebounce";

interface LocationSuggestion {
  place_id: string;
  description: string;
  structured_formatting?: {
    main_text: string;
    secondary_text: string;
  };
}

export interface LocationData {
  address: string;
  lat?: number;
  lng?: number;
  place_id?: string;
}

interface LocationAutocompleteProps {
  value?: string;
  defaultValue?: string;
  onChange: (location: LocationData | null) => void;
  placeholder?: string;
  className?: string;
  showClearButton?: boolean;
  autoFocus?: boolean;
  disabled?: boolean;
  label?: string;
  error?: string;
  helperText?: string;
  inputId?: string;
}

export const LocationAutocomplete: React.FC<LocationAutocompleteProps> = ({
  value,
  defaultValue,
  onChange,
  placeholder = "Enter address, zip code, or place name",
  className = "",
  showClearButton = true,
  autoFocus = false,
  disabled = false,
  label,
  error,
  helperText,
  inputId = "location-autocomplete",
}) => {
  const [inputValue, setInputValue] = useState(value || defaultValue || "");
  const [suggestions, setSuggestions] = useState<LocationSuggestion[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [justSelected, setJustSelected] = useState(false);
  const justSelectedRef = useRef(false); // Ref to avoid stale closures in onBlur
  const [lastGeocodedValue, setLastGeocodedValue] = useState<string>("");
  const [sessionToken] = useState(() =>
    Math.random().toString(36).substring(7),
  );

  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const debouncedSearchTerm = useDebounce(inputValue, 300);

  // Update input value when prop changes
  useEffect(() => {
    if (value !== undefined && value !== inputValue) {
      setInputValue(value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]); // Intentionally excluding inputValue to prevent infinite loops

  // Fetch suggestions when search term changes
  useEffect(() => {
    const fetchSuggestions = async () => {
      // Don't fetch if we just selected something or if input is too short
      if (
        !debouncedSearchTerm ||
        debouncedSearchTerm.length < 3 ||
        justSelected
      ) {
        setSuggestions([]);
        return;
      }

      setIsLoading(true);
      try {
        const results = await suggestionApi.getAddressAutocomplete(
          debouncedSearchTerm,
          sessionToken,
        );
        setSuggestions(results || []);
        // Only show suggestions if user is actively typing (not from programmatic value changes)
        if (document.activeElement === inputRef.current) {
          setShowSuggestions(true);
        }
      } catch (error) {
        console.error("Error fetching location suggestions:", error);
        setSuggestions([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSuggestions();
  }, [debouncedSearchTerm, sessionToken, justSelected]);

  // Handle click outside to close suggestions
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    setSelectedIndex(-1);
    setJustSelected(false);
    justSelectedRef.current = false;

    // If user clears input, notify parent
    if (!newValue) {
      onChange(null);
      setLastGeocodedValue(""); // Reset tracking
    }
  };

  const selectSuggestion = useCallback(
    async (suggestion: LocationSuggestion) => {
      const address = suggestion.description;
      setInputValue(address);
      setShowSuggestions(false);
      setSuggestions([]);
      setJustSelected(true);
      justSelectedRef.current = true; // Ref for onBlur closure
      setLastGeocodedValue(address);

      // Geocode the selected address to get coordinates
      try {
        const geocodeResult = await suggestionApi.geocodeAddress(address);
        // Handle both response formats (flat lat/lng or nested geometry.location)
        const lat = geocodeResult.lat ?? geocodeResult.geometry?.location?.lat;
        const lng = geocodeResult.lng ?? geocodeResult.geometry?.location?.lng;
        onChange({
          address,
          lat,
          lng,
          place_id: suggestion.place_id,
        });
      } catch (error) {
        console.error("Error geocoding address:", error);
        // Still update with address even if geocoding fails
        onChange({
          address,
          place_id: suggestion.place_id,
        });
      }

      // Reset the flag after onBlur timer (250ms) has had time to check it
      setTimeout(() => {
        setJustSelected(false);
        justSelectedRef.current = false;
      }, 500);
    },
    [onChange],
  );

  // Helper function to detect if input looks like a geocodable address
  const looksLikeAddress = (input: string): boolean => {
    if (!input || input.length < 5) return false;

    const trimmed = input.trim();

    // Check for zip code (5 digits or 5+4 format)
    if (/^\d{5}(-\d{4})?$/.test(trimmed)) return true;

    // Check for common address patterns
    const hasNumber = /\d/.test(trimmed);
    const hasLetters = /[a-zA-Z]/.test(trimmed);

    // Common address keywords
    const addressKeywords =
      /\b(street|st|avenue|ave|road|rd|drive|dr|boulevard|blvd|lane|ln|way|court|ct|circle|cir|place|pl|parkway|pkwy|highway|hwy)\b/i;
    const hasAddressKeyword = addressKeywords.test(trimmed);

    // City/State patterns
    const hasCityState = /,\s*[A-Z]{2}\b/.test(trimmed); // ", CA" or ", NY"
    const hasZipInString = /\b\d{5}\b/.test(trimmed);

    // Must have both numbers and letters for a street address
    // OR be a city/state combo
    // OR contain address keywords
    return (
      (hasNumber && hasLetters) ||
      hasCityState ||
      hasZipInString ||
      hasAddressKeyword
    );
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showSuggestions || suggestions.length === 0) return;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setSelectedIndex((prev) =>
          prev < suggestions.length - 1 ? prev + 1 : prev,
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
        break;
      case "Enter":
        e.preventDefault();
        if (suggestions.length > 0 && showSuggestions) {
          // If suggestions are available, select the highlighted one or the first one
          const indexToSelect = selectedIndex >= 0 ? selectedIndex : 0;
          selectSuggestion(suggestions[indexToSelect]);
        } else if (inputValue && !justSelected) {
          // If no suggestions but there's input, try to geocode it
          handleDirectInput();
        }
        break;
      case "Escape":
        setShowSuggestions(false);
        break;
    }
  };

  const handleDirectInput = async () => {
    if (!inputValue || justSelected || justSelectedRef.current) return;

    // Check if it looks like a zip code
    const inputString =
      typeof inputValue === "string" ? inputValue : String(inputValue || "");
    const isZipCode = /^\d{5}(-\d{4})?$/.test(inputString.trim());

    try {
      setIsLoading(true);
      setJustSelected(true); // Prevent re-triggering
      setShowSuggestions(false);
      setSuggestions([]); // Clear suggestions
      setLastGeocodedValue(inputValue); // Track that we're geocoding this

      const searchQuery = isZipCode ? `${inputValue}, USA` : inputValue;

      console.log("[LocationAutocomplete] Geocoding address:", searchQuery);
      const geocodeResult = await suggestionApi.geocodeAddress(searchQuery);

      // Handle both response formats (flat lat/lng or nested geometry.location)
      const lat = geocodeResult?.lat ?? geocodeResult?.geometry?.location?.lat;
      const lng = geocodeResult?.lng ?? geocodeResult?.geometry?.location?.lng;

      if (!geocodeResult || !lat || !lng) {
        console.warn("[LocationAutocomplete] Geocoding returned no coordinates",
        );
        // Still update with the raw input
        onChange({ address: inputValue });
        return;
      }

      onChange({
        address: geocodeResult.formatted_address || inputValue,
        lat,
        lng,
        place_id: geocodeResult.place_id,
      });

      setInputValue(geocodeResult.formatted_address || inputValue);

      // Reset justSelected after a delay
      setTimeout(() => setJustSelected(false), 500);
    } catch (error: any) {
      console.error("[LocationAutocomplete] Error geocoding input:", error);
      console.error("[LocationAutocomplete] Error details:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });

      // Show user-friendly error in console
      if (error.response?.status === 500) {
        console.error("[LocationAutocomplete] Geocoding service error. The address may not be recognized or there may be an API configuration issue.",
        );
      }

      // Still update with the raw input - let validation handle it
      onChange({ address: inputValue });
      setTimeout(() => setJustSelected(false), 500);
    } finally {
      setIsLoading(false);
      setShowSuggestions(false);
    }
  };

  const handleClear = () => {
    setInputValue("");
    setSuggestions([]);
    setShowSuggestions(false);
    setJustSelected(false);
    justSelectedRef.current = false;
    setLastGeocodedValue("");
    onChange(null);
    inputRef.current?.focus();
  };

  return (
    <div className={`relative ${className}`} ref={wrapperRef}>
      {label && (
        <label
          htmlFor={inputId}
          className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
        >
          {label}
        </label>
      )}

      <div className="relative">
        <div className="absolute left-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
          {isLoading ? (
            <Loader2 className="w-4 h-4 text-gray-400 dark:text-gray-500 animate-spin" />
          ) : (
            <MapPin className="w-4 h-4 text-gray-400 dark:text-gray-500" />
          )}
        </div>

        <input
          ref={inputRef}
          id={inputId}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            // Only show suggestions if we have them and didn't just select something
            if (suggestions.length > 0 && !justSelected) {
              setShowSuggestions(true);
            }
          }}
          onBlur={() => {
            // Auto-geocode on blur to improve UX - allows users to type and click Next
            setTimeout(async () => {
              // Use ref to get latest value (avoids stale closure from before suggestion click)
              if (justSelectedRef.current) {
                setShowSuggestions(false);
                return;
              }

              // Check if we should auto-geocode
              const shouldGeocode =
                inputValue &&
                inputValue !== lastGeocodedValue &&
                looksLikeAddress(inputValue);

              if (shouldGeocode) {
                try {
                  await handleDirectInput();
                } catch (error: any) {
                  console.warn("[LocationAutocomplete] Auto-geocode on blur failed:",
                    error.message || error,
                  );
                }
              }
              setShowSuggestions(false);
            }, 250);
          }}
          placeholder={placeholder}
          disabled={disabled}
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus={autoFocus}
          className={`
            w-full pl-10 pr-10 py-2 border rounded-lg
            focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 focus:border-primary-500 dark:focus:border-primary-400
            ${disabled ? "bg-gray-100 dark:bg-gray-700 cursor-not-allowed" : "bg-white dark:bg-gray-800"}
            ${error ? "border-red-500 dark:border-red-400" : "border-gray-300 dark:border-gray-600"}
            text-gray-900 dark:text-gray-100
            text-ellipsis overflow-hidden whitespace-nowrap
            ${className}
          `}
        />

        {showClearButton && inputValue && !disabled && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Suggestions Dropdown */}
      {showSuggestions && suggestions.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg dark:shadow-xl max-h-60 overflow-y-auto">
          {suggestions.map((suggestion, index) => (
            <button
              key={suggestion.place_id}
              type="button"
              onClick={() => selectSuggestion(suggestion)}
              onMouseEnter={() => {
                // Only update selected index if not using keyboard navigation
                if (selectedIndex === -1) {
                  setSelectedIndex(index);
                }
              }}
              onMouseMove={() => {
                // Update on mouse move to allow proper hover highlighting
                setSelectedIndex(index);
              }}
              className={`
                w-full px-4 py-3 text-left hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors
                ${index === selectedIndex ? "bg-gray-50 dark:bg-gray-700" : ""}
                ${index !== suggestions.length - 1 ? "border-b border-gray-100 dark:border-gray-700" : ""}
              `}
            >
              <div className="flex items-start gap-3">
                <MapPin className="w-4 h-4 text-gray-400 dark:text-gray-500 mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  {suggestion.structured_formatting ? (
                    <>
                      <div className="font-medium text-gray-900 dark:text-gray-100 truncate">
                        {suggestion.structured_formatting.main_text}
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400 truncate">
                        {suggestion.structured_formatting.secondary_text}
                      </div>
                    </>
                  ) : (
                    <div className="text-gray-900 dark:text-gray-100 truncate">
                      {suggestion.description}
                    </div>
                  )}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Helper Text or Error */}
      {(helperText || error) && (
        <div className="relative z-10">
          <p
            className={`mt-2 text-sm ${error ? "text-red-600 dark:text-red-400" : "text-gray-500 dark:text-gray-400"} bg-white dark:bg-gray-950 px-1`}
          >
            {error || helperText}
          </p>
        </div>
      )}
    </div>
  );
};

export default LocationAutocomplete;
